'use client';

import { useTranslations } from 'next-intl';
import { useAppStore } from '@/features/shared/app-store';
import { GameData, PurchaseOption } from '@/features/games/shared/types/game-data';
import { PriceIncludingWildMembership, PromotionData } from '@/shared/types/prices';
import { useState } from 'react';
import Modal from '@/shared/components/overlay/modal';
import { ItemAddedOverlay } from '@/features/cart/shared/components/item-added-overlay';
import { useOverlayStore } from '@/shared/components/overlay/overlay.store';
import { useUser } from '@/features/auth/shared/hooks/use-user';
import { isAlreadyInCart } from '@/features/games/shared/helpers/is-already-in-cart';
import { useGameStore } from '@/features/games/shared/store/game-store';
import { isAlreadyRented } from '@/features/games/shared/helpers/is-already-rented';
import { PurchaseGameButton } from '@/features/games/internal/components/game-detail/download-game/purchase-game-button';
import { mapGamePrices, mapPromotionPrices } from '@/features/games/shared/helpers/map-game-prices';
import { resolveViewerPromotion } from '@/features/games/shared/helpers/resolve-viewer-promotion';
import { getEffectivePromotionalPrices } from '@/features/games/shared/helpers/get-effective-promotional-prices';
import { getPriceByType } from '@/features/games/shared/helpers/get-price-by-type';
import { selectGamePrice } from '@/features/games/shared/store/game-price-slice-selectors';
import { TrackViewItem } from '@/features/gtm/shared/components/track-view-item';
import { buyOptionToEcommerceParams } from '@/features/games/shared/helpers/buy-option-to-ecommerce-params';
import { useCurrency } from '@/features/shared/hooks/use-currency';

type Props = {
  gameData: GameData;
};

export const PurchaseGamesOptions = ({ gameData }: Props) => {
  const t = useTranslations('GamePage.purchaseGame');
  const addToCart = useAppStore((s) => s.addItem);
  const setShowOverlay = useAppStore((s) => s.setShowOverlay);
  const cartProductItems = useAppStore((s) => s.cart.items);
  const cartInitialized = useAppStore((s) => s.cartInitialized);
  const currentCurrency = useCurrency();
  const prices = useGameStore(selectGamePrice(gameData.sku));

  const { loading, authenticatedUser } = useUser();
  const isMember = !!authenticatedUser?.isWildMember;
  const mappedPrices = prices && mapGamePrices(prices, isMember, currentCurrency);
  const isTaxIncluded = prices?.taxInclusive;

  const purchaseHistory = useGameStore((s) => s.purchaseHistory);
  const showOverlay = useOverlayStore((state) => state.showOverlay);

  const [isAdding, setIsAdding] = useState<boolean>(false);
  const [purchaseOption, setPurchaseOption] = useState<PurchaseOption | null>(null);
  const [purchasePrice, setPurchasePrice] = useState<PriceIncludingWildMembership | null>(null);

  const buyOption = gameData.purchaseOptions?.find((opt) => opt.type === 'buy');
  const rentOption = gameData.purchaseOptions?.find((opt) => opt.type === 'rent');
  const buyOptionPrice = mappedPrices?.buy || [];
  const rentOptionPrice = mappedPrices?.rent || [];

  if (buyOption && buyOptionPrice) {
    buyOption.prices = buyOptionPrice;
  }
  if (rentOption && rentOptionPrice) {
    rentOption.prices = rentOptionPrice;
  }

  const promotionData = resolveViewerPromotion(prices?.promotion, isMember);
  const buyOptionPromotionalPrice = getEffectivePromotionalPrices(
    promotionData && mapPromotionPrices(promotionData.prices, isMember, currentCurrency),
    getPriceByType(buyOptionPrice, 'current')?.value,
  );
  const buyPromotion: PromotionData | undefined =
    promotionData && buyOptionPromotionalPrice
      ? {
          name: promotionData.name,
          percentage: isMember
            ? (promotionData.membershipPercentageOverride ?? promotionData.percentage)
            : promotionData.percentage,
          endDate: promotionData.endDate,
          promotionalPrices: buyOptionPromotionalPrice,
        }
      : undefined;

  const buyOptionInCart = buyOption ? isAlreadyInCart(cartProductItems, buyOption) : false;
  const rentOptionInCart = rentOption ? isAlreadyInCart(cartProductItems, rentOption) : false;

  const alreadyRented = isAlreadyRented(gameData.sku, purchaseHistory);
  const isLoading = loading || !prices || !mappedPrices;
  const isUiLoading = isLoading || !cartInitialized || isAdding;

  const handleAddToCart = async (
    purchaseOption: PurchaseOption,
    price: PriceIncludingWildMembership,
    promotion?: PromotionData,
  ) => {
    setShowOverlay(true);
    setPurchaseOption(purchaseOption);
    setPurchasePrice(price);
    setIsAdding(true);
    showOverlay('item-added-to-cart');
    try {
      await addToCart(purchaseOption, isMember, promotion);
    } finally {
      setIsAdding(false);
    }
  };

  const trackViewItemParams =
    buyOptionPrice &&
    gameData &&
    !isLoading &&
    buyOptionToEcommerceParams(
      buyOptionPrice,
      gameData,
      !!authenticatedUser?.isWildMember,
      buyPromotion,
    );

  return (
    <>
      <div className='mt-4 mb-4 flex flex-col gap-4'>
        {buyOption && (
          <>
            {!isLoading && trackViewItemParams && <TrackViewItem params={trackViewItemParams} />}
            <PurchaseGameButton
              title={t('buy')}
              purchaseOption={buyOption}
              isLoading={isUiLoading}
              inCart={buyOptionInCart || rentOptionInCart}
              prices={buyOptionPrice}
              isMember={isMember}
              blocked={isAdding}
              promotion={buyPromotion}
              taxInfo={
                isTaxIncluded && (
                  <span className='mt-2 text-xs text-white opacity-60'>* {t('includeVAT')}</span>
                )
              }
              alreadyInCartText={t('alreadyInCart')}
              addToCartAction={handleAddToCart}
            />
          </>
        )}

        {rentOption && !alreadyRented && (
          <>
            <PurchaseGameButton
              title={t('rent')}
              prices={rentOptionPrice}
              purchaseOption={rentOption} // ensure prices are included
              isLoading={isUiLoading}
              inCart={rentOptionInCart || buyOptionInCart}
              isMember={isMember}
              blocked={isAdding}
              taxInfo={
                isTaxIncluded && (
                  <span className='mt-2 text-xs text-white opacity-60'>* {t('includeVAT')}</span>
                )
              }
              alreadyInCartText={t('alreadyInCart')}
              addToCartAction={handleAddToCart}
            />
            <span className='text-xs text-white'>* {t('rentNote')}</span>
          </>
        )}
      </div>
      {purchaseOption && purchasePrice && gameData && (
        <Modal modalKey='item-added-to-cart' closeable={!isAdding}>
          <ItemAddedOverlay
            gameData={gameData}
            purchasePrice={purchasePrice}
            purchaseType={purchaseOption.type}
            closeDisabled={isAdding}
          />
        </Modal>
      )}
    </>
  );
};
